import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropType from 'prop-types';
import {
  ASSESS_IMAGES_API_ENDPOINT, JOB_TYPES, INITIAL_INSPECTION, REPORT_OPTIONS,
} from 'constants.js';
import * as sharedUtil from 'utils/utils';
import {
  EVBox,
  EVLandscapeIcon,
} from '@eagleview/ev-comp-library';
import get from 'lodash/get';
import { isEntitled } from 'utils/auth.utils';
import ReportDecision from './ReportDecisions';
import ReportRadioSection from './ReportRadio/ReportRadioSection';
import { MAIN_STRUCTURE, ROOF_DECISION, POST_REPORT } from '../Adjuster.constants';
import { ASSESS_UNDERWRITING_IMPROVEMENTS, FEATURE_ENTITLEMENTS } from '../../entitleUser/EntitleUser.constants';
import * as action from '../Adjuster.actions';

const ReportDecisionMultiStructure = ({
  orderId, structures, riskShot, decisions, structureRoofMapping, notes, styles, jobTypes,
}) => {
  const dispatch = useDispatch();
  const authToken = useSelector((state) => state.adjusterReducer.accessToken);
  const isReportJobTypeEnabled = useSelector((state) => get(state.entitleUserReducer.featureFlags, ASSESS_UNDERWRITING_IMPROVEMENTS, false));
  const loading = useSelector((state) => state.adjusterReducer.loading);
  const entitlements = useSelector((state) => state.entitleUserReducer.entitlements);
  const orderCompleted = useSelector((state) => state.adjusterReducer.orderCompleted);
  const [primaryStructureDecision, setPrimaryStructureDecision] = useState(null);
  const [selectedJobType, setSelectedJobType] = useState(REPORT_OPTIONS.CLAIM);

  const composeRiskShotURL = (urn) => `${ASSESS_IMAGES_API_ENDPOINT}/${urn}/thumb?width=170&height=128&${sharedUtil.getAuthParam(authToken)}`;

  // Radio button change handler
  const handleRadioSelectChange = (event) => {
    if (!isReportJobTypeEnabled) return;

    const newJobType = event.target.value;
    setSelectedJobType(newJobType);
    const newDecision = newJobType === REPORT_OPTIONS.CLAIM ? ROOF_DECISION.UNDECIDED : INITIAL_INSPECTION;
    const apiJobType = newJobType === REPORT_OPTIONS.CLAIM ? JOB_TYPES.CLAIMS : JOB_TYPES.UNDERWRITING;
    structures.forEach((structure) => {
      if (structure.isCaptured) {
        const structureRoofId = structureRoofMapping[structure.structureID];
        dispatch(action.saveRoofDecisionAction({
          orderId,
          roofId: structureRoofId,
          data: { decision: newDecision, jobType: apiJobType },
        }));
      }
    });
  };

  // Effect to track primary structure decision
  useEffect(() => {
    const primaryStructure = structures.find((structure) => structure.structureID === MAIN_STRUCTURE);
    if (primaryStructure) {
      const roofId = structureRoofMapping[primaryStructure.structureID];
      const decision = get(decisions, roofId, '');

      if (isReportJobTypeEnabled) {
        const jobType = get(jobTypes, roofId, '');
        // Map API jobType values to radio button values
        let mappedJobType;
        if (jobType === JOB_TYPES.CLAIMS) {
          mappedJobType = REPORT_OPTIONS.CLAIM;
        } else if (jobType === JOB_TYPES.UNDERWRITING) {
          mappedJobType = REPORT_OPTIONS.UNDERWRITING;
        } else {
          mappedJobType = REPORT_OPTIONS.CLAIM; // default
        }
        setSelectedJobType(mappedJobType);
      }

      setPrimaryStructureDecision(decision);
    }
  }, [decisions, structures, structureRoofMapping, jobTypes, isReportJobTypeEnabled]);

  return (
    <>
      {
      structures.map((structure) => {
        const { structureID, structureName, isCaptured } = structure;
        if (isCaptured) {
          const roofId = structureRoofMapping[structureID];
          const urn = get(riskShot, `${structureID}.images[0].imageURN`);
          const riskShotOrientation = get(riskShot, `${structureID}.images[0].customMeta.orientation`, 0);
          const rotateAngle = 360 - riskShotOrientation;
          // when image rotated 90/180 deg, due to aspect ratio image renders beyond the parent.
          // So we translate the image (windth-height)/2 => (170 - 128) / 2 => 21
          let translateX = 0;
          switch (Math.abs(rotateAngle % 360)) {
            case 90: translateX = 21; break;
            case 270: translateX = -21; break;
            default: translateX = 0;
          }

          const isPrimaryStructure = structureID === MAIN_STRUCTURE;
          const isDisabled = !isPrimaryStructure && primaryStructureDecision === ROOF_DECISION.UNDERWRITING;

          return (
            <div className={styles.decisionMultiStructurePanel}>
              <div className={styles.roofImgPanel}>
                <div className={styles.structureName}>{structureName}</div>
                {urn && (
                <div className={styles.roofImg}>
                  <img
                    style={{ transform: `rotate(${rotateAngle}deg) translateX(${translateX}px)` }}
                    width="170px"
                    alt="Roof"
                    src={composeRiskShotURL(urn)}
                  />
                </div>
                )}
                {!urn && (
                <EVBox
                  className={styles.roofImg}
                  width="170px%"
                  height="130px"
                  bgcolor="grey.300"
                  color="common.white"
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                >
                  <EVLandscapeIcon style={{ fontSize: 100 }} />
                </EVBox>
                )}
              </div>
              <div className={styles.decisionMultiStructure}>
                {isPrimaryStructure && isReportJobTypeEnabled && (
                  <ReportRadioSection
                    selectedOption={selectedJobType}
                    handleRadioSelectChange={handleRadioSelectChange}
                    leftOffset={1}
                    disabled={!isEntitled(entitlements, FEATURE_ENTITLEMENTS.MANAGE_DECISIONS) || orderCompleted || loading[POST_REPORT]}
                  />
                )}
                <ReportDecision
                  orderId={orderId}
                  roofId={roofId}
                  decisions={decisions}
                  notes={notes}
                  styles={styles}
                  isPrimaryStructure={isPrimaryStructure}
                  disabled={isDisabled}
                  currentJobType={selectedJobType}
                />
              </div>
            </div>
          );
        }
        return <></>;
      })
      }
    </>
  );
};

ReportDecisionMultiStructure.propTypes = {
  orderId: PropType.string.isRequired,
  structures: PropType.arrayOf(
    PropType.shape({}),
  ).isRequired,
  notes: PropType.shape({}).isRequired,
  decisions: PropType.shape({}).isRequired,
  riskShot: PropType.shape({}).isRequired,
  structureRoofMapping: PropType.shape({}).isRequired,
  jobTypes: PropType.shape({}).isRequired,
  styles: PropType.shape({
    roofImg: PropType.string,
    roofImgPanel: PropType.string,
    decisionMultiStructure: PropType.string,
    decisionMultiStructurePanel: PropType.string,
    structureName: PropType.string,
    radioSection: PropType.string,
    radioGroup: PropType.string,
    radioContainer: PropType.string,
    radioLabel: PropType.string,
    label: PropType.string,
  }).isRequired,
};

export default ReportDecisionMultiStructure;
